import * as React from 'react';

import { useLockFn, useMemoizedFn } from 'ahooks';
import { Flex, Form, notification } from 'antd';
import { useNavigate } from 'react-router-dom';

import { PrimitiveForm } from 'components';
import { useTagAction, useTagValue } from 'context';
import { createTag, updateTag } from 'services';
import { replaceExtraSeparator } from 'utils';
import { PATHNAME } from 'utils';

import { TagFormAction } from './action';
import { TagFormTabs } from './tabs';

const {
  LOCAL: {
    TAGS: { BASE_URL: TAGS_BASE_URL },
  },
} = PATHNAME;

type FieldType = Pick<ReturnType<typeof useTagValue>, 'status'>;

const TagForm = () => {
  const [form] = Form.useForm<FieldType>();
  const {
    id = '',
    title = '',
    slug,
    description,
    seo,
    defaultTitle,
    defaultStatus,
    mode,
    step,
    categories = [],
  } = useTagValue();
  const action = useTagAction();
  const navigate = useNavigate();

  const formattedSlug = replaceExtraSeparator(slug ?? title);

  const handleUpdateTagBaseInfo = useMemoizedFn(() => {
    action({
      type: 'UPDATE_TAG_STEP',
      payload: {
        step: 2,
      },
    });
  });

  const handleUpdateTagSeoInfo = useLockFn(
    async ({ status = defaultStatus! }: FieldType) => {
      const isUpdatedMode = mode === 'Update';

      const params: Parameters<typeof updateTag>[0] = {
        id,
      };
      const body: Parameters<typeof updateTag>[1] = {
        title,
        slug: formattedSlug,
        categoriesIds: categories.map(({ id }) => id),
        description,
        seo,
        status,
      };

      (isUpdatedMode ? updateTag(params, body) : createTag(body))
        .then(() => {
          notification.success({
            message: `برچسب ${isUpdatedMode ? defaultTitle : title} با موفقیت ${isUpdatedMode ? 'ویرایش' : 'ساخته'} شد.`,
          });
          navigate(TAGS_BASE_URL);
        })
        .catch(message => {
          notification.error({ message });
        });
    },
  );

  const onFinish = useLockFn(async (fields: FieldType) => {
    switch (step) {
      case 1: {
        return handleUpdateTagBaseInfo();
      }
      case 2: {
        return handleUpdateTagSeoInfo(fields);
      }
      default:
        break;
    }
  });

  return (
    <PrimitiveForm
      form={form}
      fields={[
        {
          name: 'title',
          value: title,
        },
        {
          name: 'slug',
          value: formattedSlug,
        },
        {
          name: 'categories',
          value: categories?.map(({ id, title }) => ({
            key: id,
            label: title,
            value: id,
          })),
        },
      ]}
      onFinish={onFinish}
    >
      <Flex
        className='*:flex-1 *:rounded *:bg-white max-xl:flex-col max-xl:last:*:sticky max-xl:last:*:inset-x-0 max-xl:last:*:bottom-0 max-xl:last:*:z-10 max-xl:last:*:shadow-inner xl:items-start xl:first:*:max-w-[70%] xl:last:*:max-w-[30%]'
        gap='large'
      >
        <TagFormTabs />
        <TagFormAction />
      </Flex>
    </PrimitiveForm>
  );
};

export { TagForm };
